import { createContext, useState } from "react";
import { warningBlackIcon } from "../assets";

/**
 * React context for managing modal alerts and notifications
 * Provides centralized modal state management across the application
 */
export const ModalAlertContext = createContext();

/**
 * Modal alert provider component
 * Manages modal states and provides modal control functions to child components
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component with modal context
 */
export const ModalAlertProvider = ({ children }) => {

  /** @type {Object|null} Current modal alert configuration */
  const [modalAlert, setModalAlert] = useState(null);

  /** @type {Object|null} Please wait modal configuration */
  const [pleaseWait, setPleaseWait] = useState(false);

  /** @type {Object|null} Text modal alert configuration */
  const [textModalAlert, setTextModalAlert] = useState(null);

  /** @type {Object|null} Rate service alert configuration */
  const [rateOurServiceAlert, setRateOurServiceAlert] = useState(null);

  /**
   * Show modal alert with customizable options
   * @param {Object} options - Modal configuration options
   * @param {string} [options.title="Title Here"] - Modal title
   * @param {string} [options.message] - Modal message content
   * @param {string} [options.icon=warningBlackIcon] - Modal icon
   * @param {string} [options.type="info"] - Modal type (info, error, warning, success)
   * @param {string} [options.cancelText="Cancel"] - Cancel button text
   * @param {string} [options.confirmText="Confirm"] - Confirm button text
   * @param {string} [options.position="horizontal"] - Button layout position
   * @param {Function} [options.onCancel] - Cancel button callback
   * @param {Function|boolean} [options.onConfirm=true] - Confirm button callback
   * @param {React.ReactNode} [options.footer=null] - Custom footer content
   * @returns {void}
   */
  const showModalAlert = (options) => {
    const {
      title = "Title Here",
      message = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Varius mi id dignissim pulvinar. In.Lorem ipsum dolor sit amet, consectetur adipiscing elit. Varius mi id dignissim pulvinar. In.",
      icon = warningBlackIcon,
      type = "info",
      cancelText = "Cancel",
      confirmText = "Confirm",
      position = "horizontal",
      onCancel = () => {
        closeModalAlert();
      },
      onConfirm = true,
      footer = null
    } = options;

    setModalAlert({ title, message, icon, type, cancelText, confirmText, position, onCancel, onConfirm, footer });

  };

  /**
   * Show please wait modal with loading state
   * @param {Object} options - Please wait modal configuration
   * @param {string} [options.title="Title Here"] - Modal title
   * @param {string} [options.message="Information and connection are being checked"] - Loading message
   * @param {Object} [options.customStyle={}] - Custom modal styles
   * @param {Object} [options.customContentStyle={}] - Custom content styles
   * @returns {void}
   */
  const showPleaseWait = (options) => {

    const {
      title = "Title Here",
      message = 'Information and connection are being checked',
      customStyle = {},
      customContentStyle = {}
    } = options;
    setPleaseWait({ title, message, customStyle, customContentStyle });
  };

  /**
   * Show text modal alert with OK button
   * @param {Object} options - Text modal configuration
   * @param {string} [options.title="Title Here"] - Modal title
   * @param {string} [options.message] - Modal message content
   * @param {string} [options.html] - Modal html content
   * @param {string} [options.okText="OK"] - OK button text
   * @param {string} [options.cancelText="Cancel"] - Cancel button text
   * @param {Function} [options.onCancel] - Cancel button callback
   * @param {Function} [options.onOk] - OK button callback
   * @returns {void}
   */
  const showTextModalAlert = (options) => {
    const {
      title = "Title Here",
      message = "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Varius mi id dignissim pulvinar. In.Lorem ipsum dolor sit amet, consectetur adipiscing elit. Varius mi id dignissim pulvinar. In.",
      html = null,
      okText = "OK",
      cancelText = "Cancel",
      onCancel = () => {
        closeTextModalAlert();
      },
      onOk = () => {
        closeTextModalAlert();
      }
    } = options;
    setTextModalAlert({ title, message, html, okText, cancelText, onCancel, onOk });
  };

  /**
   * Close modal alert
   * @returns {void}
   */
  const closeModalAlert = () => {
    setModalAlert(null);
  };

  /**
   * Close please wait modal
   * @returns {void}
   */
  const closePleaseWait = () => {
    setPleaseWait(null);
  };

  /**
   * Close text modal alert
   * @returns {void}
   */
  const closeTextModalAlert = () => {
    setTextModalAlert(null);
  };

  /**
   * Show rate our service alert
   * @param {Object} options - Rate service alert options
   * @returns {void}
   */
  const showRateOurServiceAlert = (options) => {
    setRateOurServiceAlert(true);
  };

  /**
   * Close rate our service alert
   * @returns {void}
   */
  const closeRateOurServiceAlert = () => {
    setRateOurServiceAlert(null);
  };

  return (
    <ModalAlertContext.Provider
      value={
        {
          modalAlert,
          setModalAlert,
          showModalAlert,
          pleaseWait,
          showPleaseWait,
          closePleaseWait,
          closeModalAlert,
          showTextModalAlert,
          closeTextModalAlert,
          textModalAlert,
          rateOurServiceAlert,
          showRateOurServiceAlert,
          closeRateOurServiceAlert,
        }
      }>
      {children}
    </ModalAlertContext.Provider>
  );
};